import { createStore, type ReadOnlyStore } from "@observe-tech/mfe-shell-api";
import {
  bootstrapSessionWithRetry,
  type BootDecision,
  type BootstrapDeps,
} from "./offlineBoot";

export interface BootControllerDeps extends BootstrapDeps {
  isAuthExempt(): boolean;
  start(): void;
}

export type BootFailure = "unreachable" | "error";

export interface BootStatus {
  failed: ReadOnlyStore<BootFailure | null>;
  retry(): Promise<BootDecision>;
}

export interface BootController extends BootStatus {
  run(): Promise<BootDecision>;
}

export function createBootController(deps: BootControllerDeps): BootController {
  const failed = createStore<BootFailure | null>(null);
  let inFlight: Promise<BootDecision> | null = null;

  const boot = async (): Promise<BootDecision> => {
    try {
      const decision = await bootstrapSessionWithRetry(deps);
      if (decision === "unreachable" || decision === "error") {
        failed.set(decision);
        return decision;
      }
      failed.set(null);
      if (decision !== "login" || deps.isAuthExempt()) deps.start();
      return decision;
    } catch (error) {
      console.error("[shell] boot failed:", error);
      failed.set("error");
      return "error";
    }
  };

  const run = () => {
    inFlight ??= boot().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  return { failed: failed.store, run, retry: run };
}
